import i18n from '../../../i18n';
import type { ReservationSourceId, SourceSubChannelKpis } from '../types';

// Mirrors server/opsCommand/reservationSource.ts SUB_CHANNEL_NONE.
export const SUB_CHANNEL_NONE_KEY = '__none__';

const SUB_CHANNEL_KEYS: Record<string, string> = {
  [SUB_CHANNEL_NONE_KEY]: 'none',
  google: 'google',
  website: 'website',
  bing: 'bing',
  app: 'app',
  extnet: 'extnet',
  meta: 'meta',
  'chatgpt.com': 'chatgpt',
  fb: 'fb',
  ig: 'ig',
  tiktok: 'tiktok',
  newsletter: 'newsletter',
  qrcode: 'qrcode',
  speisekarte: 'speisekarte',
};

function subChannelLabel(key: string): string {
  const mapped = SUB_CHANNEL_KEYS[key];
  if (mapped) return i18n.t(`chrome:reservationSources.sub.${mapped}`);
  return key;
}

// Client-side presentation metadata for the canonical reservation sources. The
// source IDs are the shared contract with the server (see
// server/opsCommand/reservationSource.ts); labels/colors/icons live here so the
// client bundle never imports server code.
function sourceEntry(
  id: ReservationSourceId,
  color: string,
  icon: string,
): { label: string; short: string; color: string; icon: string } {
  return {
    get label() {
      return i18n.t(`chrome:reservationSources.${id}.label`);
    },
    get short() {
      return i18n.t(`chrome:reservationSources.${id}.short`);
    },
    color,
    icon,
  };
}

export const SOURCE_META: Record<
  ReservationSourceId,
  { label: string; short: string; color: string; icon: string }
> = {
  widget: sourceEntry('widget', '#4c6ef5', 'web'),
  reserveWithGoogle: sourceEntry('reserveWithGoogle', '#22a06b', 'pin'),
  assistentAi: sourceEntry('assistentAi', '#06b6d4', 'zap'),
  staff: sourceEntry('staff', '#8b5cf6', 'users'),
  walkIn: sourceEntry('walkIn', '#f59e0b', 'activity'),
  integration: sourceEntry('integration', '#d6336c', 'plug'),
  other: sourceEntry('other', '#94a3b8', 'info'),
  unattributed: sourceEntry('unattributed', '#64748b', 'info'),
};

/** Booking-origin filter order — excludes POS-only `unattributed`. */
export const SOURCE_ORDER: ReservationSourceId[] = [
  'widget',
  'reserveWithGoogle',
  'assistentAi',
  'staff',
  'walkIn',
  'integration',
  'other',
];

/** Sources whose rows open into sub-channels: widget UTM sources, the
 * integrations by name, and other by createdFrom. */
export function hasSourceChannels(id: ReservationSourceId): boolean {
  return id === 'widget' || id === 'integration' || id === 'other';
}

export function sourceMeta(id: ReservationSourceId) {
  return SOURCE_META[id] ?? SOURCE_META.other;
}

// Resolve a sub-channel label for display. Prefers the server-provided label but
// falls back to client metadata so rows like "No attribution" never render blank.
export function subChannelDisplayLabel(
  source: ReservationSourceId,
  sub: Pick<SourceSubChannelKpis, 'key' | 'label'>,
): string {
  const key = sub.key?.trim() || SUB_CHANNEL_NONE_KEY;
  if (source === 'widget') {
    if (!key || key === SUB_CHANNEL_NONE_KEY) {
      return i18n.t('chrome:reservationSources.sub.none');
    }
    if (SUB_CHANNEL_KEYS[key]) return subChannelLabel(key);
  }
  if (key === SUB_CHANNEL_NONE_KEY) {
    return i18n.t('chrome:reservationSources.sub.unknownOrigin');
  }
  const trimmed = sub.label?.trim();
  if (trimmed) return trimmed;
  return key || i18n.t('chrome:reservationSources.sub.unknown');
}
